<template>
	<header class="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
		<nav class="container px-4 mx-auto" aria-label="Hauptnavigation">
			<div class="flex items-center justify-between h-16">
				<!-- Logo/Brand -->
				<router-link
					to="/"
					class="flex items-center gap-3 px-2 -ml-2 transition-all duration-200 rounded-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
					title="Zur Startseite"
					aria-label="Schule Leichter - Zur Startseite"
				>
					<img :src="LogoSvg" alt="Schule Leichter Logo" class="w-35" />
				</router-link>

				<!-- Desktop Navigation -->
				<div class="items-center hidden gap-1 md:flex">
					<router-link
						v-for="link in mainLinks"
						:key="link.path"
						:to="link.path"
						class="px-4 py-2 font-medium text-gray-700 transition-colors duration-200 rounded-lg hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
						:class="{ 'text-blue-600 bg-blue-50': isActive(link.path) }"
					>
						{{ link.label }}
					</router-link>
				</div>

				<!-- Mobile Menu Button -->
				<button
					class="p-2 text-gray-700 transition-colors duration-200 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
					:aria-label="mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'"
					:aria-expanded="mobileMenuOpen"
					@click="toggleMobileMenu"
				>
					<svg
						v-if="!mobileMenuOpen"
						class="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M4 6h16M4 12h16M4 18h16"
						/>
					</svg>
					<svg
						v-else
						class="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>
			</div>

			<!-- Mobile Menu -->
			<transition
				name="mobile-menu"
				enter-active-class="transition duration-200 ease-out"
				enter-from-class="-translate-y-2 opacity-0"
				enter-to-class="translate-y-0 opacity-100"
				leave-active-class="transition duration-150 ease-in"
				leave-from-class="translate-y-0 opacity-100"
				leave-to-class="-translate-y-2 opacity-0"
			>
				<div v-if="mobileMenuOpen" class="pb-4 border-t border-gray-200 md:hidden">
					<!-- Main Navigation Links -->
					<div class="py-2 space-y-1">
						<router-link
							v-for="link in allLinks"
							:key="link.path"
							:to="link.path"
							class="block px-3 py-2 text-base font-medium text-gray-700 transition-colors duration-200 rounded-lg hover:text-blue-600 hover:bg-blue-50"
							:class="{ 'text-blue-600 bg-blue-50': isActive(link.path) }"
							@click="closeMobileMenu"
						>
							{{ link.label }}
						</router-link>
					</div>

					<!-- Legal Links -->
					<div class="pt-4 mt-4 border-t border-gray-200">
						<p class="px-3 mb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
							Rechtliches
						</p>
						<router-link
							v-for="link in legalLinks"
							:key="link.path"
							:to="link.path"
							class="block px-3 py-2 text-sm text-gray-600 transition-colors duration-200 rounded-lg hover:text-blue-600 hover:bg-blue-50"
							@click="closeMobileMenu"
						>
							{{ link.label }}
						</router-link>
					</div>
				</div>
			</transition>
		</nav>
	</header>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import LogoSvg from '@/assets/logo.svg?url';

interface NavLink {
	path: string;
	label: string;
}

const route = useRoute();
const mobileMenuOpen = ref(false);

// Main navigation links (shown in desktop nav)
const mainLinks: NavLink[] = [
	{ path: '/info/lerntypen', label: 'Lerntypen' },
	{ path: '/info/lernschwierigkeiten', label: 'Lernschwierigkeiten' },
	{ path: '/info/hausaufgaben-tipps', label: 'Hausaufgaben-Tipps' },
];

// All navigation links (shown in mobile menu)
const allLinks = computed<NavLink[]>(() => [{ path: '/', label: 'Startseite' }, ...mainLinks]);

// Legal links
const legalLinks: NavLink[] = [
	{ path: '/impressum', label: 'Impressum' },
	{ path: '/datenschutz', label: 'Datenschutz' },
	{ path: '/agb', label: 'AGB' },
];

// Check if route is active
const isActive = (path: string): boolean => {
	return route.path === path || route.path.startsWith(path + '/');
};

// Toggle mobile menu
const toggleMobileMenu = () => {
	mobileMenuOpen.value = !mobileMenuOpen.value;
};

// Close mobile menu
const closeMobileMenu = () => {
	mobileMenuOpen.value = false;
};

// Close mobile menu when route changes
import { watch } from 'vue';
watch(
	() => route.path,
	() => {
		closeMobileMenu();
	},
);
</script>

<style scoped>
/* Ensure smooth transitions */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
	transform-origin: top;
}
</style>
